import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { localeNames, localeFlags, isRTL, getDirection } from '../../i18n';
import gsap from 'gsap';

export default function LanguageSwitcher() {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const menuRef = useRef(null);
  const menuItemsRef = useRef([]);

  const currentLang = i18n.language?.split('-')[0] || 'id';
  const languages = Object.entries(localeNames);

  useEffect(() => {
    // Update document direction when language changes
    document.documentElement.dir = getDirection(currentLang);
    document.documentElement.lang = currentLang;
  }, [currentLang]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Animate dropdown
  useEffect(() => {
    const menu = menuRef.current;
    const menuItems = menuItemsRef.current;

    if (isOpen) {
      // Show and animate dropdown open
      if (menu) {
        gsap.set(menu, { display: 'block' });
        gsap.fromTo(
          menu,
          { opacity: 0, scale: 0.95, y: -10 },
          { opacity: 1, scale: 1, y: 0, duration: 0.2, ease: 'power2.out' }
        );
      }

      // Stagger animate menu items
      if (menuItems.length > 0) {
        gsap.fromTo(
          menuItems.filter(Boolean),
          { opacity: 0, x: -10 },
          { opacity: 1, x: 0, duration: 0.2, stagger: 0.03, delay: 0.05, ease: 'power2.out' }
        );
      }
    } else {
      // Animate dropdown close
      if (menu) {
        gsap.to(menu, {
          opacity: 0,
          scale: 0.95,
          y: -10,
          duration: 0.15,
          ease: 'power2.in',
          onComplete: () => {
            gsap.set(menu, { display: 'none' });
          },
        });
      }
    }
  }, [isOpen]);

  const changeLanguage = (lang) => {
    i18n.changeLanguage(lang);
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-1 px-3 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors"
        aria-label="Change language"
      >
        <span className="text-lg">{localeFlags[currentLang]}</span>
        <span className="text-sm font-medium text-gray-700 hidden sm:inline">
          {localeNames[currentLang]?.split(' ')[0] || 'ID'}
        </span>
        <svg
          className={`w-4 h-4 text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Animated Dropdown */}
      <div
        ref={menuRef}
        className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-100 py-1 z-50 origin-top-right"
        style={{ display: 'none' }}
      >
        {languages.map(([code, name], index) => (
          <button
            key={code}
            ref={(el) => (menuItemsRef.current[index] = el)}
            onClick={() => changeLanguage(code)}
            className={`w-full flex items-center space-x-3 px-4 py-2 text-left hover:bg-gray-50 transition-colors ${
              currentLang === code ? 'bg-primary-50 text-primary-700' : 'text-gray-700'
            }`}
          >
            <span className="text-lg">{localeFlags[code]}</span>
            <span className="text-sm">{name}</span>
            {currentLang === code && (
              <svg className="w-4 h-4 ml-auto text-primary-500" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                  clipRule="evenodd"
                />
              </svg>
            )}
          </button>
        ))}
      </div>
    </div>
  );
}
